import { DatePipe } from '@angular/common';
import { Injectable } from '@angular/core';
import { IMobileDevice } from '@app/certificates/devices/devices';
import { HistoryOrder, SigningHistory } from '@app/certificates/history/history';
import { ICertificateData } from '@app/certificates/manage/manage';
import { environment } from '@env/environment';
import { TranslateService } from '@ngx-translate/core';
import { LocalizedCurrencyPipe } from '../pipes/localized-currency-pipe';

@Injectable()
export class MatTableHelper {

    dateFormat = environment.dateFormat;
    dateTimeFormat = environment.dateTimeFormat;
    pageSize = 5;
    pageSizeOptions = [5, 10, 20, 50, 100];

    datePipe: DatePipe = new DatePipe(this.translateService.currentLang);
    currencyPipe: LocalizedCurrencyPipe = new LocalizedCurrencyPipe(this.translateService);

    constructor(private translateService: TranslateService) {
    }

    isMatched(data: any, filter: string): boolean {
        if (!data || !filter) {
            return false;
        }
        return data.toString().toLowerCase().indexOf(filter.toLowerCase()) >= 0;
    }

    filterPredicateForOrders = (order: HistoryOrder, filter: string) => {
        if (this.isMatched(order.certSerialNumber, filter)) {
            return true;
        }

        if (this.isMatched(order.orderCode, filter)) {
            return true;
        }

        if (this.isMatched(order.planName, filter)) {
            return true;
        }

        if (this.isMatched(order.planSigningCount, filter)) {
            return true;
        }

        const priceStr = this.currencyPipe.transform(order.price);
        // we should replace unicode non-breaking space
        priceStr.replace(/\u00A0/g, ' ');
        if (this.isMatched(priceStr, filter)) {
            return true;
        }

        const orderDate: Date = new Date(order.orderDate);
        const orderDateStr = this.datePipe.transform(orderDate, this.dateTimeFormat);

        if (this.isMatched(orderDateStr, filter)) {
            return true;
        }

        const paymentDate: Date = new Date(order.paymentDate);
        const paymentDateStr = this.datePipe.transform(paymentDate, this.dateTimeFormat);

        return this.isMatched(paymentDateStr, filter);
    }

    sortingDataAccessorForOrders = (order: HistoryOrder, property: string) => {
        if (property === 'status') {
            return order.status.key;
        }
        return order[property];
    }

    sortingDataAccessorForCerts = (cert: ICertificateData, property: string) => {
        if (property === 'owner') {
            return cert.owner[this.translateService.currentLang];
        }

        return cert[property];
    }

    sortingRemainingCountsForCerts = (cert: ICertificateData, property: string) => {
        if (property === 'remainingCount' && cert.packType === 'INFINITE') {
            return Number.MAX_SAFE_INTEGER;
        }

        return cert.remainingCount;
    }

    filterPredicateForSigningHistory = (data: SigningHistory, filter: string) => {
        if (this.isMatched(data.certSerialNumber, filter)) {
            return true;
        }

        if (this.isMatched(data.documentName, filter)) {
            return true;
        }

        if (this.isMatched(data.relyingPartyName, filter)) {
            return true;
        }

        const signingDate = new Date(data.date);
        const orderDateStr = this.datePipe.transform(signingDate, this.dateTimeFormat);

        return this.isMatched(orderDateStr, filter);
    }

    filterPredicateForDevices = (data: IMobileDevice, filter: string) => {
        if (this.isMatched(data.name, filter)) {
          return true;
        }

        if (this.isMatched(data.appId, filter)) {
          return true;
        }

        if (this.isMatched(data.registrationDate, filter)) {
            return true;
        }

        if (this.isMatched(data.lastUpdateDate, filter)) {
            return true;
        }

        const regDate = new Date(data.registrationDate);
        const updateDate = new Date(data.lastUpdateDate);

        const mobileDateStr = this.datePipe.transform(regDate, this.dateTimeFormat);
        const mobileDateLastUpdate = this.datePipe.transform(updateDate, this.dateTimeFormat);

        if (this.isMatched(mobileDateStr, filter)) {
            return true;
        }

        return this.isMatched(mobileDateLastUpdate, filter);
    }

    filterPredicateForCertificates = (data: ICertificateData, filter: string) => {
        if (this.isMatched(data.serialNo, filter)) {
            return true;
        }
        if (this.isMatched(data.email, filter)) {
            return true;
        }
        if (this.isMatched(data.serialNo, filter)) {
            return true;
        }

        if (this.isMatched(data.commonName, filter)) {
            return true;
        }

        if (this.isMatched(data.owner[this.translateService.currentLang], filter)) {
            return true;
        }

        const signingDate = new Date(data.validTo);
        const orderDateStr = this.datePipe.transform(signingDate, this.dateFormat);

        return this.isMatched(orderDateStr, filter);
    }
}
